import DataRow from '@/libs/antd/components/DataRow';
import FleetTitle from '@/libs/antd/components/Title';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import {
	Alert,
	Button,
	Col,
	DatePicker,
	Divider,
	Form,
	Input,
	InputNumber,
	Radio,
	Row,
	Select,
	Table,
	TableProps,
	Upload,
	UploadFile,
} from 'antd';
import {
	ArrowLeftOutlined,
	DeleteOutlined,
	EditOutlined,
	FileSearchOutlined,
	LoadingOutlined,
	UploadOutlined,
} from '@ant-design/icons';
import { FC, Fragment, useMemo, useState, useEffect } from 'react';
import rule from '@/libs/antd/rules';
import { useModals } from '@/components/modals/ModalProvider';
import FacilityModal, { useFacilityModal } from './FacilityModal';
import CovenantModal, { useCovenantModal } from './CovenantModal';
import {
	GetSPFDetailParam,
	SPFCovenantPayload,
	SPFFacilityPayload,
	SPFFaclityNotificationPayload,
	SubmitSPFPayload,
	SubmitSPFRejectPayload,
	useGetSPFDetail,
	useSubmitSPFApprovalMutation,
	useSubmitSPFMutation,
	useSubmitSPFRejectMutation,
	useSubmitSPFReviewMutation,
	useSubmitSPFDeviationMutation,
} from '@/libs/fleet-api/services/spf';
import dayjs, { Dayjs } from 'dayjs';
import { useForm } from '@/libs/antd/form';
import { formatCurrency, formatCurrencyWithDot } from '@/libs/antd/formater';
import { useUserData } from '@/libs/fleet-api/hooks';
import { useApp } from '@/App';
import { MasterData } from '@/libs/fleet-api/services/master-data';
import { FileData } from '@/libs/fleet-api/services/types';
import { useMutation } from '@tanstack/react-query';
import { uploader } from '@/libs/fleet-api/uploader';
import { NAPNJFModal, useNAPNJFModal } from '../nap/NAPDownloadPage';
import { NAPJFModal, useNAPJFModal } from '../nap/NAPJFDownloadPage';
import { inputCurrency } from '@/libs/antd/input-number';

export type SPFPageProps = GetSPFDetailParam & {
	allowDeviation?: boolean;
	onBack?: () => void;
	viewOnly?: boolean;
};

type NotificationTableRecord = {
	subjectColumn: string;
	requestColumn: string;
	approvalColumn:
	| {
		type: 'view';
		value: string;
	}
	| {
		type: 'text';
		name: keyof SPFFaclityNotificationPayload;
		value: string;
	}
	| {
		type: 'number';
		name: keyof SPFFaclityNotificationPayload;
		value: number;
	}
	| {
		type: 'date';
		name: keyof SPFFaclityNotificationPayload;
		value: string;
	}
	| {
		type: 'select';
		name: keyof SPFFaclityNotificationPayload;
		value: number;
		options: MasterData[];
	}
	| {
		type: 'select-multiple';
		name: keyof SPFFaclityNotificationPayload;
		value: number;
		options: MasterData[];
	};
};

const SPFPage: FC<SPFPageProps> = ({
	prospectId = undefined,
	spfId,
	napResolutionId,
	allowDeviation = false,
	onBack,
	viewOnly = false,
}) => {
	const { navigate } = useApp();
	const {
		modal,
		openCustomerGroupDetail,
		openPreviewModal,
		openCustomerDetail,
		openProspectDetail,
	} = useModals();
	const userData = useUserData();
	const { groupName } = userData ?? {};
	const { data, isFetching } = useGetSPFDetail({
		prospectId: prospectId ?? undefined,
		spfId,
		napResolutionId,
	});

	const submitSPFMutation = useSubmitSPFMutation();
	const submitSPFReviewMutation = useSubmitSPFReviewMutation();
	const submitSPFApprovalMutation = useSubmitSPFApprovalMutation();
	const submitSPFRejectMutation = useSubmitSPFRejectMutation();
	const submitSPFDeviationMutation = useSubmitSPFDeviationMutation();

	const isReviewing = groupName === 'FBDH' || groupName === 'CCDH';
	const isViewOnly = viewOnly || groupName === 'FCOH' || groupName === 'FCDVS';
	const isFCOHReadOnly = groupName === 'FCOH';

	const isProspectRejected =
		data?.data?.napResolutionId === null || data?.data?.napResolutionId === 0;

	const isSubmitting =
		submitSPFMutation.isPending ||
		submitSPFReviewMutation.isPending ||
		submitSPFApprovalMutation.isPending ||
		submitSPFRejectMutation.isPending ||
		submitSPFDeviationMutation.isPending;

	const {
		customerId,
		// prospectId,
		customerName,
		bmppGroupName,
		bmppGroupId,
		portfolioGuideline,
		napNo,
		ultimateOwner,
		applicationDate,
		rkpDate,
		resolutionFullApprovedDate,
		requestStatus,
		facilities,
		facilityNotificationDetails,
		covenants,
		frmNotes,
		requestEditCovenant,
		approvalHistory,
		rejectCategory,
		napPlafonTypeId,
		resolutionFile,
		approvalActionOptions,
		assetTypeOptions,
		financingObjectConditionOptions,
		financingLimitTypeOptions,
		financingTypeOptions,
		plafonTypeOptions,
		tenorOptions,
		insuranceOptions,
		categoryOptions,
		priorityOptions,
		repeatFrequencyOptions,
		customRepeatFrequencyOptions,
		monitoringType,
		furtherAnalysisOptions,
		facilityTypeOptions,
		monthOptions,
		positionOptions,
		rejectReason,
		dayOfWeekOptions,
		wayOfPaymentOptions,
		rejectCategoryOptions,
		racDeviationDocument,
	} = data?.data ?? {};

	const statusOptions = [
		{
			id: 'done',
			value: 'Done',
		},
		{
			id: 'pending',
			value: 'Pending',
		},
	] as unknown as MasterData[];

	const isRejected =
		requestStatus === 2 && napResolutionId !== null && napResolutionId !== 0;

	const { form, fieldName, value } = useForm<SubmitSPFPayload>();
	const facilityModal = useFacilityModal();
	const covenantModal = useCovenantModal();
	const napNJFModal = useNAPNJFModal();
	const napJFModal = useNAPJFModal();

	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	// Section validation states
	const [sectionValidationErrors, setSectionValidationErrors] = useState<{
		detailFasilitas: string[];
	}>({
		detailFasilitas: [],
	});

	// Dynamic financing types state for each notification
	const [dynamicFinancingTypes, setDynamicFinancingTypes] = useState<{
		[notificationIndex: number]: number[];
	}>({});

	// Initialize dynamic financing types with initial data
	useEffect(() => {
		const initialTypes: { [notificationIndex: number]: number[] } = {};
		facilityNotificationDetails?.forEach((data, notificationIndex) => {
			const { approvalFacilityNotifications } = data;
			initialTypes[notificationIndex] =
				approvalFacilityNotifications?.financingTypes?.map((type) => type.id) ??
				[];
		});
		setDynamicFinancingTypes(initialTypes);
	}, [facilityNotificationDetails]);

	const notificationTables = useMemo(() => {
		return (
			facilityNotificationDetails?.map((data, notificationIndex) => {
				const { approvalFacilityNotifications, requestFacilityNotifications } =
					data;

				const isMultiguna =
					dynamicFinancingTypes[notificationIndex]?.includes(3) ||
					(financingTypeOptions?.find((option) => option.id === 3)?.value ===
						'Pembiayaan Multiguna' &&
						dynamicFinancingTypes[notificationIndex]?.some(
							(id) =>
								financingTypeOptions?.find((option) => option.id === id)
									?.value === 'Pembiayaan Multiguna',
						));

				const isNewFacility =
					requestFacilityNotifications?.requestedLimit === 0;

				const dataSource = [
					{
						subjectColumn: 'Jenis Fasilitas',
						requestColumn: isNewFacility
							? '-'
							: isRejected
								? '-'
								: requestFacilityNotifications?.facilityTypes
									?.map((option) => option.value)
									?.join(', ') ?? '-',
						approvalColumn:
							groupName === 'CCA'
								? {
									type: 'select-multiple',
									name: 'facilityTypeIds',
									options: facilityTypeOptions,
									value:
										approvalFacilityNotifications?.facilityTypes?.map(
											(type) => type.id,
										) ?? [],
								}
								: {
									type: 'view',
									value: isRejected
										? '-'
										: approvalFacilityNotifications?.facilityTypes
											.map((option) => option.value)
											.join(',') || '-',
								},
					},
					{
						subjectColumn: 'Jenis Pembiayaan',
						requestColumn: isNewFacility
							? '-'
							: isRejected
								? '-'
								: requestFacilityNotifications?.financingTypes
									?.map((option) => option.value)
									.join(', ') ?? '-',
						approvalColumn:
							groupName === 'CCA'
								? {
									type: 'select-multiple',
									name: 'financingTypeIds',
									options: financingTypeOptions,
									value:
										approvalFacilityNotifications?.financingTypes?.map(
											(option) => option.id,
										) || [],
								}
								: {
									type: 'view',
									value: isRejected
										? '-'
										: approvalFacilityNotifications?.financingTypes
											?.map((option) => option.value)
											.join(',') || '-',
								},
					},
					{
						subjectColumn: 'Limit Pembiayaan',
						requestColumn: isNewFacility
							? '-'
							: requestFacilityNotifications.requestedLimit
								? formatCurrency(requestFacilityNotifications.requestedLimit)
								: '-',
						approvalColumn:
							groupName === 'CCA'
								? {
									name: 'approvedLimit',
									type: 'number',
									value: approvalFacilityNotifications?.approvedLimit,
								}
								: {
									type: 'view',
									value: approvalFacilityNotifications?.approvedLimit
										? formatCurrency(
											approvalFacilityNotifications?.approvedLimit,
										)
										: '-',
								},
					},
					{
						subjectColumn: 'Sifat Limit Pembiayaan',
						requestColumn: isNewFacility
							? '-'
							: financingLimitTypeOptions?.find(
								(option) =>
									option.id ===
									requestFacilityNotifications.financingLimitTypeId,
							)?.value ?? '-',
						approvalColumn:
							groupName === 'CCA'
								? {
									type: 'select',
									name: 'financingLimitTypeId',
									options: financingLimitTypeOptions,
									value: financingLimitTypeOptions?.find(
										(option) =>
											option.id ===
											approvalFacilityNotifications?.financingLimitTypeId,
									),
								}
								: {
									type: 'view',
									value:
										financingLimitTypeOptions?.find(
											(option) =>
												option.id ===
												approvalFacilityNotifications?.financingLimitTypeId,
										)?.value ?? '-',
								},
					},
					{
						subjectColumn: 'Jenis Plafond',
						requestColumn: isNewFacility
							? '-'
							: plafonTypeOptions?.find(
								(option) =>
									option.id === requestFacilityNotifications.plafonTypeId,
							)?.value ?? '-',
						approvalColumn:
							groupName === 'CCA'
								? {
									type: 'select',
									name: 'plafonTypeId',
									options: plafonTypeOptions,
									value: plafonTypeOptions?.find(
										(option) =>
											option.id === requestFacilityNotifications.plafonTypeId,
									),
								}
								: {
									type: 'view',
									value:
										plafonTypeOptions?.find(
											(option) =>
												option.id ===
												approvalFacilityNotifications?.plafonTypeId,
										)?.value ?? '-',
								},
					},
					{
						subjectColumn: 'Way Of Payment',
						requestColumn: isNewFacility
							? '-'
							: wayOfPaymentOptions?.find(
								(option) =>
									option.id === requestFacilityNotifications.wayOfPaymentId,
							)?.value ?? '-',
						approvalColumn: isRejected
							? {
								type: 'view',
								value:
									wayOfPaymentOptions?.find(
										(option) =>
											option.id ===
											approvalFacilityNotifications?.wayOfPaymentId,
									)?.value ?? '-',
							}
							: {
								type: 'select',
								name: 'wayOfPaymentId',
								value: approvalFacilityNotifications?.wayOfPaymentId,
								options: wayOfPaymentOptions,
								rules: [rule.required()],
							},
					},
					{
						subjectColumn: 'Masa Berlaku Fasilitas',
						requestColumn: isNewFacility
							? '-'
							: isRejected
								? '-'
								: requestFacilityNotifications.facilityValidityPeriod
									? dayjs(
										requestFacilityNotifications.facilityValidityPeriod,
									).format('DD/MM/YYYY')
									: '-',
						approvalColumn:
							groupName === 'CCA'
								? {
									type: 'date',
									name: 'facilityValidityPeriod',
									value: isRejected
										? '-'
										: approvalFacilityNotifications?.facilityValidityPeriod,
								}
								: {
									type: 'view',
									value: isRejected
										? '-'
										: approvalFacilityNotifications?.facilityValidityPeriod
											? dayjs(
												approvalFacilityNotifications?.facilityValidityPeriod,
											).format('DD/MM/YYYY')
											: '-',
								},
					},
					...(isMultiguna
						? [
							{
								subjectColumn: 'Mekanisme Pencairan',
								requestColumn: isNewFacility
									? '-'
									: requestFacilityNotifications.disbursementMechanism ?? '-',
								approvalColumn: {
									type: 'text',
									name: 'disbursementMechanism',
									value: approvalFacilityNotifications?.disbursementMechanism,
								},
							},
							{
								subjectColumn: 'Kondisi Objek Pembiayaan',
								requestColumn:
									requestFacilityNotifications.financingObjectCondition ??
									'-',
								approvalColumn: {
									type: 'text',
									name: 'financingObjectCondition',
									value:
										approvalFacilityNotifications?.financingObjectCondition,
								},
							},
							{
								subjectColumn: 'Penilaian Objek Pembiayaan',
								requestColumn:
									requestFacilityNotifications.financingObjectEvaluation ??
									'-',
								approvalColumn: {
									type: 'text',
									name: 'financingObjectEvaluation',
									value:
										approvalFacilityNotifications?.financingObjectEvaluation,
								},
							},
						]
						: []),
				] as NotificationTableRecord[];

				const width = isRejected ? '50%' : `${100 / 3}%`;
				const tableProps: TableProps<NotificationTableRecord> = {
					dataSource,
					columns: [
						{
							width,
							title: 'Perihal',
							render: (_: unknown, record: NotificationTableRecord) =>
								record.subjectColumn,
						},
						{
							width,
							title: 'Keterangan Pengajuan',
							render: (_: unknown, record: NotificationTableRecord) => (
								<b>{record.requestColumn}</b>
							),
						},
						...(!isRejected
							? [
								{
									width,
									title: 'Keterangan Approval',
									render: (_: unknown, record: NotificationTableRecord) => {
										if (
											record.approvalColumn?.type === 'select' ||
											record.approvalColumn.type === 'select-multiple'
										) {
											const { name } = record.approvalColumn;
											return (
												<Select
													mode={
														record.approvalColumn.type === 'select-multiple'
															? 'multiple'
															: undefined
													}
													style={{ width: '100%' }}
													disabled={
														isReviewing || isViewOnly || isFCOHReadOnly
													}
													defaultValue={record.approvalColumn.value}
													options={
														record.approvalColumn.options?.map((option) => ({
															label: option.value,
															value: option.id,
														})) ?? []
													}
													onChange={(id) => {
														form.setFieldValue(
															fieldName([
																'facilityNotifications',
																notificationIndex,
																name,
															]),
															id,
														);

														// Update dynamic financing types state for multiguna detection
														if (name === 'financingTypeIds') {
															setDynamicFinancingTypes((prev) => ({
																...prev,
																[notificationIndex]: Array.isArray(id)
																	? id
																	: [id].filter(Boolean),
															}));
														}
													}}
												/>
											);
										}

										if (record.approvalColumn.type === 'number') {
											const { name } = record.approvalColumn;
											return (
												<InputNumber
													{...inputCurrency}
													defaultValue={record.approvalColumn.value}
													disabled={
														isReviewing || isViewOnly || isFCOHReadOnly
													}
													onChange={(value) => {
														form.setFieldValue(
															fieldName([
																'facilityNotifications',
																notificationIndex,
																name,
															]),
															value,
														);
													}}
												/>
											);
										}

										if (record.approvalColumn.type === 'text') {
											const { name } = record.approvalColumn;
											return (
												<Input.TextArea
													defaultValue={record.approvalColumn.value}
													disabled={
														isReviewing || isViewOnly || isFCOHReadOnly
													}
													rows={2}
													onChange={(e) => {
														form.setFieldValue(
															fieldName([
																'facilityNotifications',
																notificationIndex,
																name,
															]),
															e.target.value,
														);
													}}
												/>
											);
										}

										if (record.approvalColumn.type === 'date') {
											const { name } = record.approvalColumn;
											return (
												<DatePicker
													defaultValue={
														record.approvalColumn.value &&
														dayjs(record.approvalColumn.value)
													}
													disabled={
														isReviewing || isViewOnly || isFCOHReadOnly
													}
													onChange={(date) => {
														console.log(date);
														form.setFieldValue(
															fieldName([
																'facilityNotifications',
																notificationIndex,
																name,
															]),
															dayjs(date).toISOString(),
														);
													}}
												/>
											);
										}

										return record.approvalColumn.value;
									},
								},
							]
							: []),
					],
				};

				return tableProps;
			}) ?? []
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		groupName,
		allowDeviation,
		facilityNotificationDetails,
		financingLimitTypeOptions,
		financingTypeOptions,
		plafonTypeOptions,
		wayOfPaymentOptions,
		dynamicFinancingTypes,
	]);

	// Form validation types
	interface FormValidationValues {
		facilities?: SPFFacilityPayload[];
		facilityNotifications?: SPFFaclityNotificationPayload[];
		covenants?: SPFCovenantPayload[];
		notes?: string;
		action?: string;
		deviationDocument?: FileData;
		rejectCategoryId?: number;
		rejectReason?: string;
	}

	// Function to get detailed validation errors grouped by section
	const getDetailedValidationErrors = async (): Promise<{
		sections: string[];
		details: string[];
		sectionErrors: {
			detailFasilitas: string[];
		};
	}> => {
		const sections: string[] = [];
		const details: string[] = [];
		const sectionErrors = {
			detailFasilitas: [] as string[],
		};
		const formValues = form.getFieldsValue() as FormValidationValues;

		// Validate Detail Fasilitas (facilities)
		if (!isRejected) {
			const facilities = formValues.facilities || [];
			const facilityErrors: string[] = [];
			const facilityNotificationErrors: string[] = [];

			// Check facilities
			if (facilities.length === 0) {
				facilityErrors.push('Minimal satu fasilitas wajib ditambahkan');
			} else {
				// Check each facility for required fields
				let missingFieldsCount = 0;
				facilities.forEach((facility: SPFFacilityPayload) => {
					const requiredFields = [
						'assetTypeId',
						'financingObjectConditionId',
						'plafonTypeId',
						'tenorId',
						'interestRateIndicationPercent',
						'downPaymentPercent',
						'administrativeFee',
						'provisionFee',
						'serviceFee',
						'insuranceId',
						'fiduciaryFee',
					];

					// Security deposit is not required for FRM group
					if (groupName !== 'FRM') {
						requiredFields.push('securityDeposit');
					}

					let facilityMissingFields = 0;
					requiredFields.forEach((key) => {
						const fieldValue = facility[key as keyof SPFFacilityPayload];
						// Check if field is empty (handles both string and number fields)
						let isEmpty = false;

						if (fieldValue === null || fieldValue === undefined) {
							isEmpty = true;
						} else if (typeof fieldValue === 'string' && fieldValue === '') {
							isEmpty = true;
						} else if (typeof fieldValue === 'number' && fieldValue === 0) {
							isEmpty = true;
						}

						if (isEmpty) {
							facilityMissingFields++;
						}
					});

					if (facilityMissingFields > 0) {
						missingFieldsCount++;
					}
				});

				if (missingFieldsCount > 0) {
					facilityErrors.push(
						`${missingFieldsCount} fasilitas memiliki field yang belum diisi lengkap`,
					);
				}
			}

			// Check facility notifications (Way of Payment)
			const facilityNotifications = formValues.facilityNotifications || [];
			let missingWayOfPayment = 0;
			facilityNotifications.forEach(
				(notification: SPFFaclityNotificationPayload) => {
					if (!notification.wayOfPaymentId) {
						missingWayOfPayment++;
					}
				},
			);

			if (missingWayOfPayment > 0) {
				facilityNotificationErrors.push(
					`${missingWayOfPayment} pemberitahuan fasilitas belum memilih Way of Payment`,
				);
			}

			// Combine facility and notification errors for Detail Fasilitas section
			const combinedFacilityErrors = [
				...facilityErrors,
				...facilityNotificationErrors,
			];
			if (combinedFacilityErrors.length > 0) {
				sections.push('Detail Fasilitas: Ada field yang belum diisi');
				details.push(...combinedFacilityErrors);
				sectionErrors.detailFasilitas = combinedFacilityErrors;
			}

			// Validate required fields based on user group and form state
			const generalErrors: string[] = [];

			if (groupName === 'FRM') {
				// Notes are required for FRM
				if (!formValues.notes || formValues.notes.trim() === '') {
					generalErrors.push('Notes wajib diisi');
				}

				// Deviation document is required if allowDeviation is true or FRM is uploading replacement document
				if (
					(allowDeviation || (groupName === 'FRM' && racDeviationDocument)) &&
					!formValues.deviationDocument
				) {
					generalErrors.push('Upload Deviasi RAC wajib diupload');
				}
			}

			// Validate approval fields for reviewing groups
			if (isReviewing || groupName === 'CCA') {
				if (!formValues.action) {
					generalErrors.push('Approval action wajib dipilih');
				}
				if (!formValues.notes || formValues.notes.trim() === '') {
					generalErrors.push('Approval Notes wajib diisi');
				}
			}

			if (generalErrors.length > 0) {
				sections.push('Form Utama: Ada field yang belum diisi');
				details.push(...generalErrors);
			}
		}

		// Validate reject fields for rejected status
		if (isRejected && groupName === 'FRM') {
			const rejectErrors: string[] = [];

			if (!formValues.rejectCategoryId) {
				rejectErrors.push('Reject Category wajib dipilih');
			}
			if (!formValues.rejectReason || formValues.rejectReason.trim() === '') {
				rejectErrors.push('Detail Alasan Penolakan wajib diisi');
			}

			if (rejectErrors.length > 0) {
				sections.push('Data Reject: Ada field yang belum diisi');
				details.push(...rejectErrors);
			}
		}

		return { sections, details, sectionErrors };
	};

	if (isFetching || !userData) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				gap: 20,
			}}
		>
			<NAPNJFModal {...napNJFModal} />
			<NAPJFModal {...napJFModal} />
			<div style={{ display: 'flex', gap: 8 }}>
				{typeof onBack === 'function' && (
					<button
						style={{
							display: 'flex',
							height: 44,
							width: 44,
							fontSize: 24,
							border: 'none',
							alignItems: 'center',
							justifyContent: 'center',
							background: '#1557BC',
							color: 'white',
							cursor: 'pointer',
						}}
						onClick={onBack}
					>
						<ArrowLeftOutlined />
					</button>
				)}
				<FleetTitle style={{ width: '100%' }}>Main Information</FleetTitle>
			</div>
			<DataRow
				items={[
					[
						[
							'Nama Customer',
							customerId ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										openCustomerDetail(customerId);
									}}
								>
									{customerName}
								</a>
							) : (
								'-'
							),
						],
						[
							'Prospect ID',
							prospectId ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										openProspectDetail(prospectId);
									}}
								>
									{prospectId}
								</a>
							) : (
								'-'
							),
						],
					],
					[
						[
							'Group Customer BMPP',
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									if (!bmppGroupId) return;
									openCustomerGroupDetail(bmppGroupId);
								}}
							>
								{bmppGroupName}
							</a>,
						],
						['Kelas Industri', 'Pertambangan'],
					],
					[
						[
							'NAP ID',
							napNo ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										if (!prospectId) {
											modal.confirm({
												title: 'Cannot navigate to NAP',
												cancelText: 'OK',
												okButtonProps: { style: { display: 'none' } },
											});
											return;
										}

										navigate('NAPPage', {
											prospectId,
											onBack: () => {
												navigate('SPFPage', {
													prospectId,
													napResolutionId,
													allowDeviation,
													spfId,
												});
											},
										});
									}}
									style={{
										color: prospectId ? '#1677ff' : '#bababa',
										cursor: prospectId ? 'pointer' : 'not-allowed',
										textDecoration: prospectId ? 'underline' : 'none',
									}}
								>
									{napNo}
								</a>
							) : (
								'-'
							),
						],
						['Portofolio Guideline', portfolioGuideline],
					],
					[
						[
							'Summary NAP',
							napPlafonTypeId ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										if (!prospectId) return;

										if (napPlafonTypeId === 1) {
											napNJFModal.open({
												prospectId,
											});
										}

										if (napPlafonTypeId === 2) {
											napJFModal.open({
												prospectId,
											});
										}
									}}
								>
									View Dokumen
								</a>
							) : (
								'-'
							),
						],
						['Ultimate Owner', ultimateOwner],
					],
					[
						[
							'Tanggal Permohonan',
							applicationDate ? (
								groupName === 'CCA' && !isFCOHReadOnly ? (
									<DatePicker
										defaultValue={dayjs(applicationDate)}
										allowClear={false}
										disabled={isFCOHReadOnly}
										onCalendarChange={(value) => {
											form.setFieldValue(
												fieldName('applicationDate'),
												(value as Dayjs).toISOString(),
											);
										}}
									/>
								) : (
									dayjs(applicationDate).format('DD/MM/YYYY')
								)
							) : (
								'-'
							),
						],
						[
							'Tanggal RKP',
							rkpDate ? (
								groupName === 'CCA' && !isFCOHReadOnly ? (
									<DatePicker
										defaultValue={dayjs(rkpDate)}
										allowClear={false}
										disabled={isFCOHReadOnly}
										onCalendarChange={(value) => {
											form.setFieldValue(
												fieldName('rkpDate'),
												(value as Dayjs).toISOString(),
											);
										}}
									/>
								) : (
									dayjs(rkpDate).format('DD/MM/YYYY')
								)
							) : (
								'-'
							),
						],
					],
					[
						[
							'Tanggal Risalah Full Approve',
							resolutionFullApprovedDate ? (
								groupName === 'CCA' && !isFCOHReadOnly ? (
									<DatePicker
										defaultValue={dayjs(resolutionFullApprovedDate)}
										allowClear={false}
										disabled={isFCOHReadOnly}
										onCalendarChange={(value) => {
											form.setFieldValue(
												fieldName('resolutionFullApprovedDate'),
												(value as Dayjs).toISOString(),
											);
										}}
									/>
								) : (
									dayjs(resolutionFullApprovedDate).format('DD/MM/YYYY')
								)
							) : (
								'-'
							),
						],
						[
							'Summary Risalah',
							resolutionFile ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										openPreviewModal(resolutionFile);
									}}
								>
									View Dokumen
								</a>
							) : (
								'-'
							),
						],
					],
					[
						[
							'History SPF',
							spfId ? (
								<a
									href="#"
									className="trigger-popup"
									onClick={(e) => {
										e.preventDefault();
										navigate('MKLHistorySPFPage', {
											spfId,
											onBack: () =>
												navigate('SPFPage', {
													spfId,
													napResolutionId,
													prospectId,
													onBack,
													viewOnly,
												}),
										});
									}}
								>
									View History
								</a>
							) : (
								'-'
							),
						],
					],
				]}
			/>
			<FleetTitle level={3}>Pemberitahuan Fasilitas</FleetTitle>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				labelCol={{ span: 6 }}
				wrapperCol={{ span: 18 }}
				style={{
					display: 'flex',
					flexDirection: 'column',
					gap: 20,
				}}
				onSubmitCapture={async () => {
					// Enhanced validation with detailed error reporting
					try {
						await form.validateFields();
						// Clear section validation errors on successful validation
						setSectionValidationErrors({
							detailFasilitas: [],
						});
					} catch {
						// Extract detailed validation errors grouped by section
						const { sections, details, sectionErrors } =
							await getDetailedValidationErrors();

						// Update section validation state
						setSectionValidationErrors(sectionErrors);

						if (sections.length > 0) {
							// Show section-based validation modal
							modal.confirm({
								title: 'Validasi Form Belum Lengkap',
								subTitle: (
									<div style={{ textAlign: 'left' }}>
										{/* Detailed errors */}
										<div>
											<ul style={{ paddingLeft: 20, margin: '8px 0 0 0' }}>
												{details.map((detail, index) => (
													<li
														key={index}
														style={{ marginBottom: 4, fontSize: '13px' }}
													>
														{detail}
													</li>
												))}
											</ul>
										</div>
									</div>
								),
								cancelText: 'OK',
								okButtonProps: { style: { display: 'none' } },
							});
						}
						// else {
						// Fallback generic message
						// modal.confirm({
						// 	title: 'Validasi Gagal',
						// 	subTitle:
						// 		'Mohon periksa dan lengkapi semua field yang wajib diisi.',
						// 	cancelText: 'OK',
						// 	okButtonProps: { style: { display: 'none' } },
						// });
						// }
						return;
					}
				}}
				onFinish={async (values) => {
					// Clear section validation errors on successful form submission
					setSectionValidationErrors({
						detailFasilitas: [],
					});

					if (isRejected && groupName === 'FRM') {
						const payload = values as unknown as SubmitSPFRejectPayload;

						await submitSPFRejectMutation.mutateAsync({
							prospectId: prospectId!,
							napResolutionId,
							rejectCategoryId: payload.rejectCategoryId,
							rejectReason: payload.rejectReason,
						});

						await modal.success({
							title: 'Reject Berhasil!',
						});

						navigate('SPFListPage');
						return;
					}

					// prospect rejected, so submit spf reject
					if (isProspectRejected && groupName === 'FRM') {
						const payload = values as unknown as SubmitSPFRejectPayload;

						await submitSPFRejectMutation.mutateAsync({
							prospectId: prospectId!,
							napResolutionId: null,
							rejectCategoryId: payload.rejectCategoryId,
							rejectReason: payload.rejectReason,
						});
						await modal.success({
							title: 'Reject Berhasil!',
						});
						navigate('SPFListPage');

						return;
					}

					const approvalValue =
						approvalActionOptions?.find(
							(option) => (option.id as unknown as string) === values.action,
						)?.value ?? '';

					const isConfirmed = await modal.confirm({
						title: (
							<strong style={{ fontSize: 16 }}>
								{isReviewing
									? `Apakah Anda yakin ingin ${approvalValue} SPF ?`
									: 'Apakah Anda yakin ingin submit SPF'}
							</strong>
						),
						cancelText: 'TIDAK',
						okText: 'YA',
					});

					if (!isConfirmed) return;

					const getSubmitPayload = () => {
						if (groupName === 'CCA') {
							delete values.napResolutionId;
							delete values.requestEditCovenant;
							values.facilities = [];
						}
						// For CCDH return actions on request edit risalah, ensure proper routing to CCA
						else if (
							groupName === 'CCDH' &&
							values.action === 'return' &&
							values.requestEditCovenant
						) {
							// Add workflow hint to ensure return goes to CCA instead of FRM
							(
								values as SubmitSPFPayload & { workflowTargetRole?: string }
							).workflowTargetRole = 'CCA';
						}

						if (values.deviationDocument) {
							values.deviationDocument = {
								id: values.deviationDocument.id,
								name: values.deviationDocument.name,
								path: values.deviationDocument.path,
							};
						}
						return values;
					};

					if (isReviewing) {
						const res = await submitSPFApprovalMutation.mutateAsync({
							id: spfId!,
							action: values.action!,
							notes: values.notes,
						});

						if (groupName === 'CCDH' && value?.action === 'approve') {
							await modal.success({
								title: (
									<strong style={{ fontSize: 20 }}>
										Berikut adalah SPF ID Anda
									</strong>
								),
								subTitle: (
									<strong style={{ fontSize: 24, color: '#1557BC' }}>
										{res.data}
									</strong>
								),
								cancelButtonProps: {
									hidden: false,
								},
								cancelText: 'BACK',
								okText: 'NEXT',
							});
						} else {
							await modal.success({
								title: `Approval Sukses!`,
							});
						}
						navigate('SPFListPage');
					} else if (groupName === 'CCA') {
						await submitSPFReviewMutation.mutateAsync(getSubmitPayload());
						await modal.success({
							title: `Approval Sukses!`,
							okText: 'BACK',
						});
						navigate('SPFListPage');
					} else {
						// Use deviation endpoint if deviation document exists
						if (values.deviationDocument) {
							await submitSPFDeviationMutation.mutateAsync(getSubmitPayload());
						} else {
							await submitSPFMutation.mutateAsync(getSubmitPayload());
						}
						await modal.success({
							title: `Approval Sukses!`,
							okText: 'BACK',
						});
						navigate('SPFListPage');
					}
				}}
			>
				<Form.Item
					name={fieldName('id')}
					initialValue={spfId}
					noStyle
				/>
				<Form.Item
					name={fieldName('napResolutionId')}
					initialValue={napResolutionId}
					noStyle
				/>
				<Form.Item
					name={fieldName('applicationDate')}
					initialValue={applicationDate}
					noStyle
				/>
				<Form.Item
					name={fieldName('rkpDate')}
					initialValue={rkpDate}
					noStyle
				/>
				<Form.Item
					name={fieldName('resolutionFullApprovedDate')}
					initialValue={resolutionFullApprovedDate}
					noStyle
				/>
				{/* Pre-fill deviation document for returned deviations */}
				<Form.Item
					name={fieldName('deviationDocument')}
					initialValue={
						// Pre-fill with existing RAC document if user is FRM and document exists
						// This ensures FRM can see previously uploaded document when deviation is returned
						racDeviationDocument && groupName === 'FRM'
							? {
								id: racDeviationDocument.id,
								name: racDeviationDocument.name,
								path: racDeviationDocument.path,
								url: racDeviationDocument.url,
								format: racDeviationDocument.format,
							}
							: undefined
					}
					noStyle
				/>
				<Form.Item label="Status Pengajuan">
					<Radio.Group
						style={{ display: 'flex' }}
						value={requestStatus || (isProspectRejected ? 2 : 1)}
					>
						<Radio
							value={1}
							style={{ lineHeight: '1' }}
							disabled
						>
							Disetujui
						</Radio>
						<Radio
							value={2}
							style={{ lineHeight: '1' }}
							disabled
						>
							Ditolak
						</Radio>
					</Radio.Group>
				</Form.Item>

				<Divider style={{ margin: 0 }} />

				{!isProspectRejected && facilityNotificationDetails ? (
					<Fragment>
						<Form.Item
							name={fieldName('facilityNotifications')}
							rules={[
								{
									validator: (_, value) => {
										if (!isRejected && value && value.length > 0) {
											for (let i = 0; i < value.length; i++) {
												const notification = value[i];
												if (!notification.wayOfPaymentId) {
													return Promise.reject(
														new Error(
															`Plafon ${i + 1}: Way of Payment wajib dipilih`,
														),
													);
												}
											}
										}
										return Promise.resolve();
									},
								},
							]}
							initialValue={
								facilityNotificationDetails?.map(
									({ approvalFacilityNotifications }) => {
										const data = approvalFacilityNotifications;

										return {
											id: data?.id,
											approvedLimit: data?.approvedLimit,
											disbursementMechanism: data?.disbursementMechanism,
											facilityValidityPeriod: data?.facilityValidityPeriod,
											financingLimitTypeId: data?.financingLimitTypeId,
											financingObjectCondition: data?.financingObjectCondition,
											financingObjectEvaluation:
												data?.financingObjectEvaluation,
											financingTypeIds: data?.financingTypes?.map(
												(type) => type.id,
											),
											plafonTypeId: data?.plafonTypeId,
											requestedLimit: data?.requestedLimit,
											resolutionPlafonId: data?.resolutionPlafonId,
											wayOfPaymentId: data?.wayOfPaymentId,
										} as SPFFaclityNotificationPayload;
									},
								) ?? []
							}
							noStyle
						>
							{notificationTables.map((tableProps, tableKey) => {
								return (
									<>
										<span style={{ fontWeight: 'bold' }}>
											Limit Plafon {tableKey + 1}
										</span>
										<Table
											key={tableKey}
											bordered
											pagination={false}
											{...tableProps}
										/>
									</>
								);
							})}
						</Form.Item>

						<Divider style={{ margin: 0 }} />
					</Fragment>
				) : (
					<Fragment>
						{isProspectRejected && data?.data?.facilityNotifications
							? data.data.facilityNotifications.map((facility, index) => (
								<Fragment key={facility.id || index}>
									<span style={{ fontWeight: 'bold' }}>
										Limit Plafon {index + 1}
									</span>
									<Table
										bordered
										pagination={false}
										showHeader={true}
										columns={[
											{
												title: 'Perihal',
												dataIndex: 'label',
												key: 'label',
												width: '40%',
											},
											{
												title: 'Keterangan Pengajuan',
												dataIndex: 'value',
												key: 'value',
											},
										]}
										dataSource={[
											{
												key: 'plafonType',
												label: 'Jenis Plafon',
												value:
													plafonTypeOptions?.find(
														(option) => option.id === facility.plafonTypeId,
													)?.value || '-',
											},
											{
												key: 'financingLimit',
												label: 'Limit Pembiayaan',
												value: facility.requestedLimit
													? formatCurrency(facility.requestedLimit)
													: facility.approvedLimit
														? formatCurrency(facility.approvedLimit)
														: '-',
											},
											{
												key: 'financingLimitType',
												label: 'Sifat Limit Pembiayaan',
												value:
													financingLimitTypeOptions?.find(
														(option) =>
															option.id === facility.financingLimitTypeId,
													)?.value || '-',
											},
											{
												key: 'financingTypes',
												label: 'Jenis Pembiayaan',
												value:
													facility.financingTypes
														?.map((type) => type.value)
														.join(', ') || '-',
											},
											{
												key: 'wayOfPayment',
												label: 'Way Of Payment',
												value:
													wayOfPaymentOptions?.find(
														(option) => option.id === facility.wayOfPaymentId,
													)?.value || '-',
											},
											{
												key: 'facilityValidityPeriod',
												label: 'Masa Berlaku Fasilitas',
												value: facility.facilityValidityPeriod
													? dayjs(facility.facilityValidityPeriod).format(
														'DD/MM/YYYY',
													)
													: '-',
											},
										]}
									/>
									{index <
										(data.data.facilityNotifications?.length || 0) - 1 && (
											<Divider style={{ margin: '16px 0' }} />
										)}
								</Fragment>
							))
							: notificationTables.map((tableProps, tableKey) => {
								return (
									<>
										<span style={{ fontWeight: 'bold' }}>
											Limit Plafon {tableKey + 1}
										</span>
										<Table
											key={tableKey}
											bordered
											pagination={false}
											{...tableProps}
										/>
									</>
								);
							})}
						<Divider style={{ margin: 0 }} />
					</Fragment>
				)}

				{!isRejected && !isProspectRejected && (
					<Fragment>
						<Row
							align="middle"
							hidden={!isRejected}
						>
							<Col flex="auto">
								<strong>Detail Fasilitas</strong>
							</Col>
							{!isReviewing && !isViewOnly && !isFCOHReadOnly && (
								<Col flex="none">
									<Button
										style={{
											borderRadius: 50,
											fontSize: 12,
											height: 24,
											fontWeight: 700,
											color: '#1557BC',
											borderColor: '#1557BC',
										}}
										icon={'+'}
										onClick={async () => {
											const newFacility = await facilityModal.open({
												action: 'create',
											});

											if (!newFacility) return;
											form.setFieldValue(fieldName('facilities'), [
												...(value?.facilities ?? []),
												newFacility,
											]);
										}}
									>
										Add
									</Button>
								</Col>
							)}
						</Row>

						{/* Detail Fasilitas Validation Alert */}
						{sectionValidationErrors.detailFasilitas.length > 0 && (
							<Alert
								message={
									<ul style={{ margin: 0, paddingLeft: 20 }}>
										{sectionValidationErrors.detailFasilitas.map(
											(error, index) => (
												<li key={index}>{error}</li>
											),
										)}
									</ul>
								}
								type="warning"
								className="custom-alert"
								showIcon
								closable
								onClose={() => {
									setSectionValidationErrors((prev) => ({
										...prev,
										detailFasilitas: [],
									}));
								}}
								style={{ marginBottom: 16 }}
							/>
						)}

						<Form.Item
							name={fieldName('facilities')}
							rules={[
								{
									validator: (_, value) => {
										if (!isRejected) {
											// Check if at least one facility exists
											if (!value || value.length === 0) {
												return Promise.reject(
													new Error(
														'Detail fasilitas wajib diisi minimal satu',
													),
												);
											}

											// Check if all required fields are filled for each facility
											for (let i = 0; i < value.length; i++) {
												const facility = value[i];
												const requiredFields = [
													'assetTypeId',
													'financingObjectConditionId',
													'plafonTypeId',
													'tenorId',
													'interestRateIndicationPercent',
													'downPaymentPercent',
													'administrativeFee',
													'provisionFee',
													'serviceFee',
													'insuranceId',
													'fiduciaryFee',
												];

												// Security deposit is not required for FRM group
												// if (groupName !== 'FRM') {
												// 	requiredFields.push('securityDeposit');
												// }

												for (const fieldName of requiredFields) {
													const value =
														facility[fieldName as keyof typeof facility];
													if (
														value === null ||
														value === undefined ||
														value === ''
													) {
														return Promise.reject(
															new Error(
																`Baris ${i + 1}: Field wajib belum diisi lengkap`,
															),
														);
													}
												}
											}
										}
										return Promise.resolve();
									},
								},
							]}
							initialValue={
								facilities?.map((record) => {
									return {
										id: record.id,
										assetTypeId: record.assetType?.id,
										administrativeFee: record.administrativeFee,
										downPaymentPercent: record.downPaymentPercent,
										fiduciaryFee: record.fiduciaryFee,
										financingObjectConditionId:
											record.financingObjectCondition?.id,
										insuranceId: record.insurance?.id,
										interestRateIndicationPercent:
											record.interestRateIndicationPercent,
										plafonTypeId: record.plafonType?.id,
										financingLimitNature: record.financingLimitType?.value,
										provisionFee: record.provisionFee,
										securityDeposit: record.securityDeposit,
										serviceFee: record.serviceFee,
										tenorId: record.tenor?.id,
										insuranceDiscountPercentage:
											record.insuranceDiscountPercentage,
									} as SPFFacilityPayload;
								}) ?? []
							}
							noStyle
						>
							<Table
								scroll={{ x: 'max-content' }}
								bordered
								pagination={false}
								dataSource={
									value?.facilities?.map((record, index) => {
										return {
											...record,
											key: index + 1,
										};
									}) ?? []
								}
								columns={[
									{
										hidden: !isReviewing,
										title: 'No',
										align: 'center',
										render: (_, __, index) => index + 1,
									},
									{
										hidden: isReviewing || isViewOnly || isFCOHReadOnly,
										fixed: 'left',
										title: 'Action',
										colSpan: 2,
										width: 100,
										align: 'center',
										render: (_, record, index) => (
											<a
												onClick={async (e) => {
													e.preventDefault();
													const updatedFacility = await facilityModal.open({
														action: 'edit',
														record,
													});

													if (!updatedFacility) return;
													form.setFieldValue(
														fieldName('facilities'),
														(value?.facilities ?? []).map(
															(record, recordIdx) =>
																index === recordIdx ? updatedFacility : record,
														),
													);
												}}
											>
												<EditOutlined />
											</a>
										),
									},
									{
										hidden: isReviewing || isViewOnly || isFCOHReadOnly,
										fixed: 'left',
										colSpan: 0,
										width: 100,
										align: 'center',
										render: (_, __, index) => (
											<a
												onClick={async (e) => {
													e.preventDefault();
													const confirmDelete = await modal.confirm({
														title: 'Hapus data ini?',
													});

													if (!confirmDelete) return;
													form.setFieldValue(
														fieldName('facilities'),
														(value?.facilities ?? []).filter(
															(_, recordIdx) => index !== recordIdx,
														),
													);
												}}
											>
												<DeleteOutlined />
											</a>
										),
									},
									{
										title: 'Jenis Asset',
										render: (_, record) =>
											assetTypeOptions?.find(
												(option) => option.id === record.assetTypeId,
											)?.value ?? '-',
									},
									{
										title: 'Kondisi Obyek Pembiayaan',
										render: (_, record) =>
											financingObjectConditionOptions?.find(
												(option) =>
													option.id === record.financingObjectConditionId,
											)?.value ?? '-',
									},
									{
										title: 'Sifat Limit Pembiayaan',
										render: (_, record) => record.financingLimitNature ?? '-',
									},
									{
										title: 'Jenis Plafon',
										render: (_, record) =>
											plafonTypeOptions?.find(
												(option) => option.id === record.plafonTypeId,
											)?.value ?? '-',
									},
									{
										title: 'Tenor Pembiayaan',
										render: (_, record) =>
											tenorOptions?.find(
												(option) => option.id === record.tenorId,
											)?.value ?? '-',
									},
									{
										title: 'Indikasi Suku Bunga (Eff)',
										render: (_, record) =>
											record.interestRateIndicationPercent
												? `${formatCurrencyWithDot(record.interestRateIndicationPercent)}%`
												: '-',
									},
									{
										title: 'Down Payment',
										render: (_, record) =>
											record.downPaymentPercent
												? `${formatCurrencyWithDot(record.downPaymentPercent)}%`
												: '-',
									},
									{
										title: 'Security Deposit',
										render: (_, record) =>
											record.securityDeposit
												? formatCurrency(record.securityDeposit)
												: '-',
									},
									{
										title: 'Biaya Administrasi',
										render: (_, record) =>
											record.administrativeFee
												? formatCurrency(record.administrativeFee)
												: '-',
									},
									{
										title: 'Provisi',
										render: (_, record) =>
											record.provisionFee
												? formatCurrencyWithDot(record.provisionFee) + '%'
												: '-',
									},
									{
										title: 'Biaya Layanan',
										render: (_, record) =>
											record.serviceFee
												? `${formatCurrencyWithDot(record.serviceFee)}%`
												: '-',
									},
									{
										title: 'Asuransi',
										render: (_, record) =>
											insuranceOptions?.find(
												(option) => option.id === record.insuranceId,
											)?.value ?? '-',
									},
									{
										title: 'Diskon Asuransi',
										align: 'center',
										render: (_, record) =>
											record.insuranceDiscountPercentage
												? `${formatCurrencyWithDot(record.insuranceDiscountPercentage)}%`
												: '-',
									},
									{
										title: 'Fidusia',
										render: (_, record) =>
											record.fiduciaryFee
												? formatCurrency(record.fiduciaryFee)
												: '-',
									},
								]}
							/>
						</Form.Item>

						<Divider style={{ margin: 0 }} />

						<Row align="middle">
							<Col flex="auto">
								<strong>Syarat dan Ketentuan</strong>
							</Col>
							{groupName === 'CCA' &&
								!isReviewing &&
								!isViewOnly &&
								!isFCOHReadOnly && (
									<Col flex="none">
										<Button
											style={{
												borderRadius: 50,
												fontSize: 12,
												height: 24,
												fontWeight: 700,
												color: '#1557BC',
												borderColor: '#1557BC',
											}}
											icon={'+'}
											onClick={async () => {
												const newRecord = await covenantModal.open({
													action: 'new',
												});

												if (newRecord) {
													form.setFieldValue(fieldName('covenants'), [
														...(value?.covenants ?? []),
														newRecord,
													]);
												}
											}}
										>
											Add Another
										</Button>
									</Col>
								)}
						</Row>

						<Form.Item
							name={fieldName('covenants')}
							initialValue={
								covenants?.map((record) => {
									return {
										categoryId: record.category.id,
										covenant: record.covenant,
										dueDate: record.dueDate,
										furtherAnalysisId: record.furtherAnalysis.id,
										monitoringTypeId: record.monitoringType.id,
										priorityId: record.priority.id,
										repeatFrequencyId: record.repeatFrequency.id,
										covenantDocument: record.covenantDocument && {
											id: record.covenantDocument.id,
											name: record.covenantDocument.name,
											path: record.covenantDocument.path,
											url: record.covenantDocument.url,
										},
										id: record.id,
										statusId: record.status,
										repeatFrequencyCustom: record.repeatFrequencyCustom && {
											id: record.repeatFrequencyCustom.id,
											repeatFrequencyId:
												record.repeatFrequencyCustom.repeatFrequency.id,
											repeatValue: record.repeatFrequencyCustom.repeatValue,
											onPattern: record.repeatFrequencyCustom.onPattern && {
												dayOfMonth:
													record.repeatFrequencyCustom.onPattern.dayOfMonth,
												monthId:
													record.repeatFrequencyCustom.onPattern.month.id,
											},
											onThePattern: record.repeatFrequencyCustom
												.onThePattern && {
												dayOfWeekId:
													record.repeatFrequencyCustom.onThePattern.dayOfWeek
														.id,
												monthId:
													record.repeatFrequencyCustom.onThePattern.month.id,
												positionId:
													record.repeatFrequencyCustom.onThePattern.position.id,
											},
										},
										notes: record.notes,
										uploadDocumentDate: record.uploadDocumentDate,
									} as SPFCovenantPayload;
								}) ?? []
							}
							noStyle
						>
							<Table
								scroll={{ x: 'max-content' }}
								bordered
								pagination={false}
								dataSource={
									value?.covenants?.map((record, index) => {
										return {
											...record,
											key: index + 1,
										};
									}) ?? []
								}
								columns={[
									{
										title: 'No',
										fixed: 'left',
										align: 'center',
										render: (_, __, index) => index + 1,
									},
									{
										fixed: 'left',
										title: 'Edit',
										align: 'center',
										colSpan:
											groupName !== 'CCA' ||
												isReviewing ||
												isViewOnly ||
												isFCOHReadOnly
												? 1
												: 2,
										hidden: isReviewing || isViewOnly || isFCOHReadOnly,
										render: (_, record, index) => {
											if (groupName === 'CCA') {
												return <EditOutlined style={{ color: '#bababa' }} />;
											}
											return (
												<a
													onClick={async (e) => {
														e.preventDefault();
														const updatedCovenant = await covenantModal.open({
															action: 'edit',
															record,
														});

														if (!updatedCovenant) return;
														form.setFieldValue(
															fieldName('covenants'),
															(value?.covenants ?? []).map(
																(record, recordIdx) =>
																	index === recordIdx
																		? updatedCovenant
																		: record,
															),
														);
													}}
												>
													<EditOutlined />
												</a>
											);
										},
									},
									{
										fixed: 'left',
										title: 'Delete',
										align: 'center',
										colSpan: 0,
										hidden:
											groupName !== 'CCA' ||
											isReviewing ||
											isViewOnly ||
											isFCOHReadOnly,
										render: (_, __, index) => (
											<a
												onClick={async (e) => {
													e.preventDefault();
													const confirmDelete = await modal.confirm({
														title: 'Hapus covenant ini?',
														subTitle:
															'Data covenant yang dihapus tidak dapat dikembalikan.',
													});

													if (!confirmDelete) return;
													form.setFieldValue(
														fieldName('covenants'),
														(value?.covenants ?? []).filter(
															(_, recordIdx) => index !== recordIdx,
														),
													);
												}}
											>
												<DeleteOutlined />
											</a>
										),
									},
									{
										title: 'Covenant',
										render: (_, record) => record.covenant,
									},
									{
										title: 'Kategori',
										render: (_, record) =>
											categoryOptions?.find(
												(option) => option.id === record.categoryId,
											)?.value ?? '-',
									},
									{
										title: 'Priority',
										render: (_, record) =>
											priorityOptions?.find(
												(option) => option.id === record.priorityId,
											)?.value ?? '-',
									},
									{
										title: 'Repeat',
										render: (_, record) =>
											repeatFrequencyOptions?.find(
												(option) => option.id === record.repeatFrequencyId,
											)?.value ?? '-',
									},
									{
										title: 'Jenis Monitoring',
										render: (_, record) =>
											monitoringType?.find(
												(option) => option.id === record.monitoringTypeId,
											)?.value ?? '-',
									},
									{
										title: 'Analisa Lebih Lanjut',
										render: (_, record) =>
											furtherAnalysisOptions?.find(
												(option) => option.id === record.furtherAnalysisId,
											)?.value ?? '-',
									},
									{
										title: 'Tanggal Upload Dokumen',
										render: (_, record) =>
											record.uploadDocumentDate
												? dayjs(record.uploadDocumentDate).format('DD/MM/YYYY')
												: '-',
									},
									{
										title: 'View Dokumen',
										fixed: 'right',
										align: 'center',
										render: (_, record) => (
											<a
												onClick={(e) => {
													e.preventDefault();
													if (!record.covenantDocument?.id) return;
													openPreviewModal(record.covenantDocument);
												}}
											>
												<FileSearchOutlined
													style={{
														color: record.covenantDocument?.id
															? '#1677ff'
															: '#bababa',
													}}
												/>
											</a>
										),
									},
									{
										title: 'Status',
										render: (_, record) =>
											statusOptions?.find(
												(option) =>
													(option.id as unknown as string) === record.statusId,
											)?.value ?? '-',
									},
									{
										title: 'Due Date',
										render: (_, record) =>
											record.dueDate
												? dayjs(record.dueDate).format('DD/MM/YYYY')
												: '-',
									},
									{
										title: 'OD',
										render: (_, __, recordIndex) => {
											const od = covenants?.find(
												(_, index) => index === recordIndex,
											)?.overdueDays;

											return typeof od === 'number' ? `${od} Hari` : '-';
										},
									},
									{
										title: 'Keterangan',
										render: (_, record) => record.notes ?? '-',
									},
								]}
							/>
						</Form.Item>

						<Form.Item
							name={fieldName('requestEditCovenant')}
							label="Request Edit Syarat dan Ketentuan"
							initialValue={requestEditCovenant ?? false}
						>
							<Radio.Group style={{ display: 'flex' }}>
								<Radio
									value={true}
									disabled={groupName !== 'FRM' || isViewOnly}
									style={{ lineHeight: '1' }}
								>
									Ya
								</Radio>
								<Radio
									value={false}
									disabled={groupName !== 'FRM' || isViewOnly}
									style={{ lineHeight: '1' }}
								>
									Tidak
								</Radio>
							</Radio.Group>
						</Form.Item>

						<Divider style={{ margin: 0 }} />

						{(allowDeviation ||
							(groupName === 'FRM' && racDeviationDocument)) && (
								<Fragment>
									<Form.Item
										name={fieldName('deviationDocument')}
										label="Upload Deviasi RAC"
										rules={[rule.required()]}
										getValueProps={(value) => {
											const file = value as FileData;
											return {
												fileList: file?.id
													? ([
														{
															uid: file.id,
															name: file.name,
															thumbUrl: file.url,
														},
													] as UploadFile[])
													: [],
											};
										}}
										normalize={({ file }: { file: UploadFile }) => {
											return {
												id: file.uid,
												format: file.type,
												name: file.name,
												url: file.thumbUrl,
											} as FileData;
										}}
									>
										<Upload.Dragger
											style={{ width: 300 }}
											listType="picture"
											maxCount={1}
											beforeUpload={() => false}
											onRemove={() => {
												form.setFieldValue(fieldName('deviationDocument'), null);
												return false;
											}}
											onChange={async ({ file }) => {
												const fileid = Date.now().toString();
												const filepath = `customer/${customerId}/${prospectId!}/nap/risalah`;

												try {
													const url = await uploadMutation.mutateAsync({
														file: file as unknown as File,
														fileid,
														filepath,
													});

													form.setFieldValue(fieldName('deviationDocument'), {
														format: 'jpeg',
														id: fileid,
														name: file.name,
														path: filepath,
														url,
													});
												} catch {
													form.setFieldValue(
														fieldName('deviationDocument'),
														null,
													);
												}
											}}
										>
											<UploadOutlined
												style={{
													fontSize: 25,
													color: '#BABABA',
													display: 'block',
												}}
											/>
										</Upload.Dragger>
									</Form.Item>
									<Divider style={{ margin: 0 }} />
								</Fragment>
							)}

						{/* Display RAC Deviation Document only when there's no upload capability */}
						{racDeviationDocument &&
							!(
								allowDeviation ||
								(groupName === 'FRM' && racDeviationDocument)
							) && (
								<Fragment>
									<Form.Item label="Dokumen Deviasi RAC">
										<div
											style={{ display: 'flex', alignItems: 'center', gap: 10 }}
										>
											<span>{racDeviationDocument.name}</span>
											<a
												href="#"
												onClick={(e) => {
													e.preventDefault();
													openPreviewModal(racDeviationDocument);
												}}
												style={{
													color: '#1677ff',
													textDecoration: 'underline',
												}}
											>
												View Dokumen
											</a>
										</div>
									</Form.Item>
									<Divider style={{ margin: 0 }} />
								</Fragment>
							)}

						{isReviewing || groupName === 'CCA' || isViewOnly ? (
							<Fragment>
								<Form.Item label="Notes FRM">
									<Input.TextArea
										disabled
										rows={3}
										value={frmNotes}
									/>
								</Form.Item>
								<Divider style={{ margin: 0 }} />
							</Fragment>
						) : (
							<Form.Item
								name={fieldName('notes')}
								label="Notes"
								rules={[rule.required()]}
							>
								<Input.TextArea
									rows={3}
									disabled={isFCOHReadOnly || viewOnly}
								/>
							</Form.Item>
						)}
					</Fragment>
				)}

				{!isViewOnly && (
					<>
						<FleetTitle>Approval History</FleetTitle>

						<Table
							bordered
							pagination={false}
							dataSource={approvalHistory?.map((history, index) => ({
								...history,
								key: index,
							}))}
							columns={[
								{
									title: 'No',
									align: 'center',
									render: (_, __, index) => index + 1,
								},
								{
									title: 'Name',
									render: (_, record) => record.name,
								},
								{
									title: 'Date',
									align: 'center',
									render: (_, record) =>
										record.date && dayjs(record.date).format('DD/MM/YYYY'),
								},
								{
									title: 'Process',
									align: 'center',
									render: (_, record) => record.process,
								},
								{
									title: 'Status',
									align: 'center',
									render: (_, record) => record.status,
								},
								{
									title: 'Approval Notes',
									align: 'center',
									render: (_, record) => record.notes,
								},
							]}
						/>
					</>
				)}

				{(isReviewing || groupName === 'CCA') && (
					<Fragment>
						<Form.Item
							label="Approval"
							name={fieldName('action')}
							rules={[rule.required()]}
						>
							<Select
								style={{ width: 200 }}
								disabled={isFCOHReadOnly || isViewOnly}
								options={
									approvalActionOptions?.map((option) => {
										return {
											label: option.value,
											value: option.id,
										};
									}) ?? []
								}
							/>
						</Form.Item>

						<Divider style={{ margin: 0 }} />

						<Form.Item
							label="Approval Notes"
							name={fieldName('notes')}
							rules={[rule.required()]}
						>
							<Input.TextArea
								rows={3}
								disabled={isFCOHReadOnly || isViewOnly}
							/>
						</Form.Item>
					</Fragment>
				)}

				{isRejected && (
					<Fragment>
						{groupName === 'FBDH' || isViewOnly || isRejected ? (
							<Form.Item label="Reject Category">
								<Input
									value={rejectCategory?.value}
									disabled
								/>
							</Form.Item>
						) : (
							<Form.Item
								label="Reject Category"
								name="rejectCategoryId"
								rules={[rule.required()]}
							>
								<Select
									style={{ width: 300 }}
									disabled={isFCOHReadOnly}
									options={rejectCategoryOptions?.map((option) => {
										return {
											label: option.value,
											value: option.id,
										};
									})}
								/>
							</Form.Item>
						)}
						<Divider style={{ margin: 0 }} />

						<Form.Item
							label="Detail Alasan Penolakan"
							name="rejectReason"
							rules={groupName === 'FBDH' ? [] : [rule.required()]}
							initialValue={rejectReason}
						>
							<Input.TextArea
								rows={3}
								disabled={
									groupName === 'FBDH' ||
									isFCOHReadOnly ||
									isViewOnly ||
									isRejected
								}
							/>
						</Form.Item>
					</Fragment>
				)}

				{isProspectRejected && groupName === 'FRM' && (
					<Fragment>
						<Form.Item
							label="Reject Category"
							name="rejectCategoryId"
							rules={[rule.required()]}
							initialValue={data?.data?.rejectCategory?.id}
						>
							<Select
								style={{ width: 300 }}
								disabled={isFCOHReadOnly}
								options={rejectCategoryOptions?.map((option) => {
									return {
										label: option.value,
										value: option.id,
									};
								})}
							/>
						</Form.Item>

						<Divider style={{ margin: 0 }} />

						<Form.Item
							label="Detail Alasan Penolakan"
							name="rejectReason"
							rules={[rule.required()]}
							initialValue={rejectReason}
						>
							<Input.TextArea
								rows={3}
								disabled={isFCOHReadOnly || isViewOnly}
							/>
						</Form.Item>
					</Fragment>
				)}

				{!isViewOnly && !isFCOHReadOnly && (
					<Button
						type="primary"
						htmlType="submit"
						style={{
							width: 200,
							marginLeft: 'auto',
						}}
						loading={isSubmitting}
						disabled={isSubmitting}
					>
						SAVE
					</Button>
				)}
			</Form>

			<FacilityModal
				{...facilityModal}
				{...{
					assetTypeOptions,
					financingObjectConditionOptions,
					plafonTypeOptions,
					tenorOptions,
					insuranceOptions,
					userGroup: groupName,
				}}
			/>

			<CovenantModal
				{...covenantModal}
				{...{
					customerId,
					prospectId,
					categoryOptions,
					priorityOptions,
					repeatFrequencyOptions,
					customRepeatFrequencyOptions,
					monitoringType,
					furtherAnalysisOptions,
					monthOptions,
					positionOptions,
					dayOfWeekOptions,
					statusOptions,
				}}
			/>
		</div>
	);
};

export default SPFPage;
